import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { LazyMotion, domAnimation, MotionConfig } from "framer-motion";
import { AppShell } from "@/components/layout/AppShell";
import { ProjectLayout } from "@/components/layout/ProjectLayout";
import { ProjectSurfaceGate } from "@/components/layout/ProjectSurfaceGate";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ToastProvider } from "@/components/ui/toast";
import { queryClient } from "@/api/queryClient";
import { AuthInitializer } from "@/components/AuthInitializer";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { TermsReacceptGate } from "@/components/TermsReacceptGate";
import { MaintenanceBanner, isDeploying } from "@/components/MaintenanceBanner";
import { NewVersionAvailable } from "@/components/NewVersionAvailable";
import { ScopeChangeModal } from "@/components/ScopeChangeModal";
import { CreditBlockModal } from "@/components/CreditBlockModal";
import { EditConflictModal } from "@/components/EditConflictModal";
import { Loader2 } from "lucide-react";
import { recoverChunk } from "@/lib/chunkRecovery";

// Eagerly loaded: auth pages (small, needed immediately)
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
// Public marketing pricing page (lazy — not on the critical auth path).
const Pricing = lazy(() => import("@/pages/Pricing"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvite from "@/pages/AcceptInvite";
import Landing from "@/pages/Landing";
import NotFound from "@/pages/NotFound";
import { getAccessToken, getGuestToken } from "@/api/client";

// Lazy-loaded: all app pages (code-split per route)
// First-run activation wizard — concept->live guided journey (also mounted by
// ActivationGate for a brand-new org's founder; this route is the explicit entry).
const ActivationWizard = lazy(() =>
  import("@/components/activation/ActivationWizard").then((m) => ({ default: m.ActivationWizard })),
);
// V2 picker — uses ftl.ux.v2.home flag to choose v2 Home vs legacy Dashboard.
const HomeOrDashboard = lazy(() => import("@/pages/v2/HomeOrDashboard"));
// V3 Phase 1 — root home router (briefing-first vs v2/v1 fallback per ftl.ux.v3.briefing_landing flag).
const HomeOrV3 = lazy(() => import("@/pages/v3/HomeOrV3"));
// V3 Phase 1 — motion showcase route (designer-tunable; not in global nav).
const MotionShowcase = lazy(() => import("@/pages/v3/MotionShowcase"));
// V3 Phase 4 — CrystallizationKernel WOW visualization showcase (Patent J).
const Phase4Showcase = lazy(() => import("@/pages/motion/Phase4Showcase"));
// V3 Phase 2 — Iterator backend test surface (designer-tunable; not in global nav).
const DesignLanguage = lazy(() => import("@/pages/DesignLanguage"));
// V3 Phase 3 — Iterator MVP fullscreen route (gated by ftl.ux.v3.iterator_mvp).
const DevelopIterator = lazy(() => import("./pages/DevelopIterator"));
const Iterator = lazy(() => import("@/pages/Iterator"));
// V3 Phase 9 — DomainSelector naming + checkout (gated by
// ftl.ux.v3.domain_selector). Standalone entry until the Iterator ship-it
// seam (S2) lands; see the integration comment in src/pages/Iterator.tsx.
const DomainSelectorRoute = lazy(() => import("@/pages/DomainSelectorRoute"));

const HostingRoute = lazy(() => import("@/pages/HostingRoute"));
const GitHubAppSetup = lazy(() => import("@/pages/GitHubAppSetup"));
const SettingsIntegrations = lazy(() => import("@/pages/SettingsIntegrations"));
const EmbeddedFelixRoute = lazy(() => import("@/pages/EmbeddedFelixRoute"));
const ProgramGates = lazy(() => import("@/pages/ProgramGates"));
const ProjectBudget = lazy(() => import("@/pages/ProjectBudget"));
const OperateRoute = lazy(() => import("@/pages/OperateRoute"));
// V3 Phase 11 — public FTL Living App directory + per-app Felix changelog
// (P4 / Patent AF). Public, no auth; the directory route is always public.
// (The intended living_app_certification embed-gate was never wired — it
// existed only in comments — and its dead flag was purged 2026-07-12.)
const LivingAppsDirectory = lazy(() => import("@/pages/LivingAppsDirectory"));
// V3 Phase 15 — Broadcast (marketing automation; 6th project mode), gated by
// ftl.ux.v3.broadcast_mode.
const BroadcastRoute = lazy(() => import("@/pages/BroadcastRoute"));
// Marketing Automation — Leads pipeline (org-scoped demand-gen surface).
const LeadsSurface = lazy(() =>
  import("@/pages/marketing/LeadsSurface").then((mod) => ({ default: mod.LeadsSurface })),
);
const SegmentBuilder = lazy(() =>
  import("@/pages/marketing/SegmentBuilder").then((mod) => ({ default: mod.SegmentBuilder })),
);
const CampaignTemplates = lazy(() =>
  import("@/pages/marketing/CampaignTemplates").then((mod) => ({ default: mod.CampaignTemplates })),
);
const MarketingAnalytics = lazy(() =>
  import("@/pages/marketing/MarketingAnalytics").then((mod) => ({ default: mod.MarketingAnalytics })),
);
const ContentStudio = lazy(() =>
  import("@/pages/marketing/ContentStudio").then((mod) => ({ default: mod.ContentStudio })),
);
const ApprovalsQueue = lazy(() =>
  import("@/pages/marketing/ApprovalsQueue").then((mod) => ({ default: mod.ApprovalsQueue })),
);
const SocialComposer = lazy(() =>
  import("@/pages/marketing/SocialComposer").then((mod) => ({ default: mod.SocialComposer })),
);
// V3 nav seam — flag-aware project index (mode selector vs ProjectOverview),
// gated by ftl.ux.v3.project_mode_selector. Falls back to ProjectOverview.
const ProjectLanding = lazy(() => import("@/pages/v3/ProjectLanding"));
const Projects = lazy(() => import("@/pages/Projects"));
// Portfolio Hub — Portfolio → Program → Project constellation (primary nav).
const PortfolioHub = lazy(() => import("@/pages/PortfolioHub"));
// V3 Phase 12 — template marketplace / gallery
const MarketplaceTemplates = lazy(() => import("@/pages/MarketplaceTemplates"));
// Phase 5 briefing pages
const MyDayPage = lazy(() => import("@/pages/v2/MyDay"));
const MyWeekPage = lazy(() => import("@/pages/v2/MyWeek"));
const ProjectHealthPage = lazy(() => import("@/pages/v2/ProjectHealth"));
const SponsorUpdatePage = lazy(() => import("@/pages/v2/SponsorUpdate"));
const ProgramHealthPage = lazy(() => import("@/pages/v2/ProgramHealth"));
const RisksAndBlockersPage = lazy(() => import("@/pages/v2/RisksAndBlockers"));
const DecisionsNeededPage = lazy(() => import("@/pages/v2/DecisionsNeeded"));
const ChangeSummaryPage = lazy(() => import("@/pages/v2/ChangeSummary"));
const DeliveryTeamUpdatePage = lazy(() => import("@/pages/v2/DeliveryTeamUpdate"));
const PortfolioExceptionsPage = lazy(() => import("@/pages/v2/PortfolioExceptions"));
const Portfolios = lazy(() => import("@/pages/Portfolios"));
const Programs = lazy(() => import("@/pages/Programs"));
const ProgramDetail = lazy(() => import("@/pages/ProgramDetail"));
const PortfolioDetail = lazy(() => import("@/pages/PortfolioDetail"));
// ProjectOverview is now rendered via ProjectLanding (the flag-aware index),
// which lazy-imports it directly; no top-level App.tsx reference is needed.
const Objectives = lazy(() => import("@/pages/Objectives"));
const Modules = lazy(() => import("@/pages/Modules"));
const Components = lazy(() => import("@/pages/Components"));
const ComponentSpecifications = lazy(() => import("@/pages/ComponentSpecifications"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Tests = lazy(() => import("@/pages/Tests"));
const Defects = lazy(() => import("@/pages/Defects"));
const Documents = lazy(() => import("@/pages/Documents"));
// Founder 6467901243: the pre-demo check (the ISW demo's lessons in the product).
const DemoCheck = lazy(() => import("@/pages/DemoCheck"));
const Traceability = lazy(() => import("@/pages/Traceability"));
const Baselines = lazy(() => import("@/pages/Baselines"));
const ChangeControl = lazy(() => import("@/pages/ChangeControl"));
const Reports = lazy(() => import("@/pages/Reports"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const WebhookEndpointsPage = lazy(() => import("@/pages/WebhookEndpoints"));
const DomainPricingPage = lazy(() => import("@/pages/admin/DomainPricing"));
const PricingConfigPage = lazy(() => import("@/pages/admin/PricingConfig"));
const FeatureFlagsAdminPage = lazy(() => import("@/pages/admin/FeatureFlagsAdmin"));
const StaffingSimulationPage = lazy(() => import("@/pages/StaffingSimulation"));
const WhatGivesPage = lazy(() => import("@/pages/WhatGives"));
// Row de06a31f (founder 6559526956): FTL's own people mark up every page; the server 404s anyone else.
const FounderReviewPage = lazy(() => import("@/pages/FounderReview"));
const SkillsPage = lazy(() => import("@/pages/Skills"));
const SecretsVaultPage = lazy(() => import("@/pages/SecretsVault"));
const OrganizationSettings = lazy(() => import("@/pages/OrganizationSettings"));
const OrgFelixSteering = lazy(() => import("@/pages/OrgFelixSteering"));
const Roles = lazy(() => import("@/pages/Roles"));
const StatusRegistry = lazy(() => import("@/pages/StatusRegistry"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const Meetings = lazy(() => import("@/pages/Meetings"));
// The pages an outside guest walks through (row 75a2edb9): a download that
// fails while their network changes is retried, then the page reloads once,
// instead of leaving them on an error that "Try Again" cannot clear.
const MeetingRoom = lazy(() => import("@/pages/MeetingRoom").catch(
  recoverChunk(() => import("@/pages/MeetingRoom"), "MeetingRoom")));
const MeetingLobby = lazy(() => import("@/pages/MeetingLobby").catch(
  recoverChunk(() => import("@/pages/MeetingLobby"), "MeetingLobby")));
const MeetingJoinGuest = lazy(() => import("@/pages/MeetingJoinGuest").catch(
  recoverChunk(() => import("@/pages/MeetingJoinGuest"), "MeetingJoinGuest")));
const GuestMeetingEnd = lazy(() => import("@/pages/GuestMeetingEnd").catch(
  recoverChunk(() => import("@/pages/GuestMeetingEnd"), "GuestMeetingEnd")));
const MeetingHistory = lazy(() => import("@/pages/MeetingHistory"));
const MeetingReview = lazy(() => import("@/pages/MeetingReview"));
const Conflicts = lazy(() => import("@/pages/Conflicts"));
const Sprints = lazy(() => import("@/pages/Sprints"));
const RequirementsImport = lazy(() => import("@/pages/RequirementsImport"));
const DocumentPlan = lazy(() => import("@/pages/DocumentPlan"));
const UserProfile = lazy(() => import("@/pages/UserProfile"));
const InventorPriorExperience = lazy(() => import("@/pages/InventorPriorExperience"));
const SuccessPolicies = lazy(() => import("@/pages/SuccessPolicies"));
const Milestones = lazy(() => import("@/pages/Milestones"));
const UserGuides = lazy(() => import("@/pages/UserGuides"));
const UserStories = lazy(() => import("@/pages/UserStories"));
const UserPersonas = lazy(() => import("@/pages/UserPersonas"));
// Outcomes (dead mig-011 vertical, 0 rows ever) unrouted 2026-07-12 with the
// Outcome Record slice — one outcome surface, not two. Page file retained for
// the nav-consolidation PR to retire or repurpose (see CHANGELOG).
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const AccessControl = lazy(() => import("@/pages/AccessControl"));
const FeedbackReport = lazy(() => import("@/pages/FeedbackReport"));
const RetentionNoticePage = lazy(() => import("@/pages/RetentionNoticePage"));
const Team = lazy(() => import("@/pages/Team"));
const Billing = lazy(() => import("@/pages/Billing"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const DomainCheckoutSuccess = lazy(() => import("@/pages/DomainCheckoutSuccess"));
const DomainCheckoutCancel = lazy(() => import("@/pages/DomainCheckoutCancel"));
const BillingCancel = lazy(() => import("@/pages/BillingCancel"));
const ReleaseNotes = lazy(() => import("@/pages/ReleaseNotes"));
const OrgReports = lazy(() => import("@/pages/OrgReports"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const QASessions = lazy(() => import("@/pages/QASessions"));
const QAAgent = lazy(() => import("@/pages/QAAgent"));
const DesignSystem = lazy(() => import("@/pages/DesignSystem"));
const FelixRuns = lazy(() => import("@/pages/FelixRuns"));
const FelixRunDetail = lazy(() => import("@/pages/FelixRunDetail"));
const GitConfiguration = lazy(() => import("@/pages/GitConfiguration"));
const QADiagnostics = lazy(() => import("@/pages/QADiagnostics"));
const QAActionItems = lazy(() => import("@/pages/QAActionItems"));
const ConversationHistory = lazy(() => import("@/pages/ConversationHistory"));
const KnowledgeBase = lazy(() => import("@/pages/KnowledgeBase"));
const WorkspaceHub = lazy(() => import("@/pages/WorkspaceHub"));
const GmailPage = lazy(() => import("@/pages/Gmail"));
const CalendarPage = lazy(() => import("@/pages/Calendar"));
const AnswerQuestion = lazy(() => import("@/pages/AnswerQuestion"));
const NDASign = lazy(() => import("@/pages/NDASign"));
const ContactsPage = lazy(() => import("@/pages/Contacts"));
const DrivePage = lazy(() => import("@/pages/Drive"));
const Legal = lazy(() => import("@/pages/Legal"));
const Support = lazy(() => import("@/pages/Support"));
const KnownLimits = lazy(() => import("@/pages/KnownLimits"));

// Platform Admin pages (global admin only)
const PlatformDashboard = lazy(() => import("@/pages/platform/PlatformDashboard"));
const PlatformPatentDocket = lazy(() => import("@/pages/platform/PlatformPatentDocket"));
const PlatformOrganizations = lazy(() => import("@/pages/platform/PlatformOrganizations"));
const PlatformUsers = lazy(() => import("@/pages/platform/PlatformUsers"));
const PlatformAccessRequests = lazy(() => import("@/pages/platform/PlatformAccessRequests"));
const PlatformApiKeys = lazy(() => import("@/pages/platform/PlatformApiKeys"));
const PlatformSupportInbox = lazy(() => import("@/pages/platform/PlatformSupportInbox"));
const PlatformTrialCodes = lazy(() => import("@/pages/platform/PlatformTrialCodes"));
const PlatformFinancials = lazy(() => import("@/pages/platform/PlatformFinancials"));
const PlatformConfiguration = lazy(() => import("@/pages/platform/PlatformConfiguration"));
const PlatformFeatureFlags = lazy(() => import("@/pages/platform/PlatformFeatureFlags"));
const PlatformFelixOps = lazy(() => import("@/pages/platform/PlatformFelixOps"));
const PlatformPricing = lazy(() => import("@/pages/platform/PlatformPricing"));
const PlatformSupervisor = lazy(() => import("@/pages/platform/PlatformSupervisor"));
const PlatformFeedback = lazy(() => import("@/pages/platform/PlatformFeedback"));
const PlatformNDA = lazy(() => import("@/pages/platform/PlatformNDA"));
const PlatformProcesses = lazy(() => import("@/pages/platform/PlatformProcesses"));
const PlatformReleaseNotes = lazy(() => import("@/pages/platform/PlatformReleaseNotes"));
const PlatformReports = lazy(() => import("@/pages/platform/PlatformReports"));
const PlatformWorkspaces = lazy(() => import("@/pages/PlatformWorkspaces"));
const PlatformConversationInsights = lazy(() => import("@/pages/platform/PlatformConversationInsights"));
const PlatformFelixBenchmarks = lazy(() => import("@/pages/platform/PlatformFelixBenchmarks"));
const ProjectPlan = lazy(() => import("@/pages/ProjectPlan"));
const PlatformFelixConfig = lazy(() => import("@/pages/platform/PlatformFelixConfig"));
const PlatformFelixQuality = lazy(() => import("@/pages/platform/PlatformFelixQuality"));
const PlatformFelixSteering = lazy(() => import("@/pages/platform/PlatformFelixSteering"));
const PlatformFrictionFindings = lazy(() => import("@/pages/platform/PlatformFrictionFindings"));
const PlatformJudgeCalibration = lazy(() => import("@/pages/platform/PlatformJudgeCalibration"));
const PlatformUatPack = lazy(() => import("@/pages/platform/PlatformUatPack"));
// Phase 0 (v3) — patent submission portfolio (inventor-only; 404 for everyone else).
// Deep-link-only by design: no sidebar entry, no nav reference. Server returns
// 404 for non-inventors and the client renders a generic 404 too.
const PatentsPortfolio = lazy(() => import("@/pages/PatentsPortfolio"));


const ADMIN_ROLES = ["admin", "org_admin", "program_director"];

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center py-32">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function ProtectedPage({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/** Meeting pages allow both authenticated users and guests with guest JWTs.
 *
 *  `shell={false}` (the meeting ROOM, row 0206a645): a signed-in host gets the
 *  room full-bleed, as a guest always did. Inside the app shell the sidebar
 *  and suggestion bar took stage width, and the shell's breadcrumb showed the
 *  humanised meeting id ("Af0e92fd D680 ...") to everyone watching when the
 *  host shared the screen. */
function MeetingPage({ children, shell = true }: { children: React.ReactNode; shell?: boolean }) {
  // Accept EITHER a real FTL session token OR a guest token. Guest tokens now
  // live in their own storage slot (`ftl_guest_token`, since #1232) so they no
  // longer clobber the real session — but that means the room-entry gate must
  // read BOTH slots, else a guest who just completed guest-auth (token in the
  // guest slot, real slot empty) is bounced right back out of the room. This
  // also covers the host whose real session lapsed: they fall through to the
  // token-less branch below and get sent to /login, not a dead-end.
  const token = getAccessToken() ?? getGuestToken();
  // The MEMBER lobby (row 370236ca) is for a signed-in member only. A guest
  // JWT or no session at all is sent to the join page, which offers "Sign in"
  // first and "Join as guest" second; a guest is never shown the lobby.
  const pathParts = window.location.pathname.split("/");
  const isMemberLobby = pathParts[3] === "lobby";
  if (token) {
    // Authenticated user (regular or guest JWT) — render with layout for regular users
    // but skip AppShell for guests (they don't have nav/sidebar context)
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      if (payload.is_guest) {
        if (isMemberLobby) {
          return <Navigate to={`/join/${pathParts[2] ?? ""}`} replace />;
        }
        return <>{children}</>;
      }
    } catch {
      // malformed token — fall through to protected page
    }
    return (
      <ProtectedRoute>
        {shell ? <AppShell>{children}</AppShell> : children}
      </ProtectedRoute>
    );
  }
  // No token at all. Two distinct visitors:
  //  (a) URL carries a guest-link token (?token=…) → an external invitee.
  //      Send them to the guest join gate, which CONSUMES that token. This is
  //      the 2026-04-23 fix ("emailed the link to 3 people, only 1 joined") —
  //      external invitees don't have /login credentials.
  //  (b) NO ?token= → a signed-in FTL user whose session is missing
  //      (expired/cleared). Sending them to /join dead-ends on the guest
  //      page's "missing access token" error — it has nothing to consume.
  //      THIS was the host lockout. Send them to /login with a return path so
  //      they re-authenticate as themselves and land back in THIS meeting.
  const meetingPath = window.location.pathname.split("/")[2] ?? "";
  const qs = window.location.search || "";
  if (new URLSearchParams(qs).has("token")) {
    return <Navigate to={`/join/${meetingPath}${qs}`} replace />;
  }
  //  (c) The MEMBER LOBBY link, which calendar invites now carry (row
  //      370236ca), opened with no session: the join page, where "Sign in"
  //      (back to this lobby) comes first and "Join as guest" second.
  if (isMemberLobby) {
    return <Navigate to={`/join/${meetingPath}`} replace />;
  }
  const next = encodeURIComponent(window.location.pathname + qs);
  return <Navigate to={`/login?next=${next}`} replace />;
}

/** Root "/" — show Dashboard for authenticated users, Landing for visitors.
 *  HomeOrDashboard picks V2 Home vs legacy Dashboard based on the
 *  ftl.ux.v2.home flag (Phase 1 cutover, per plan §7). Falls back to
 *  legacy when flag is off OR while the flag map is loading. */
function HomePage() {
  const token = getAccessToken();
  if (!token) return <Landing />;
  return (
    <ProtectedRoute>
      <AppShell>
        <HomeOrV3 />
      </AppShell>
    </ProtectedRoute>
  );
}

function AdminPage({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requiredRoles={ADMIN_ROLES}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

/** An admin surface whose API is open to more than ADMIN_ROLES.
 *
 *  WHY NOT JUST WIDEN ADMIN_ROLES. Twelve routes share it. Adding a role there
 *  to fix one page silently grants that role the other eleven — the shape where
 *  fixing one of several handlers that share a guard is worse than leaving it.
 *  So the extra roles are named per route, next to the route that needs them.
 */
function AdminPageFor({
  roles,
  children,
}: {
  roles: string[];
  children: React.ReactNode;
}) {
  return (
    <ProtectedRoute requiredRoles={[...ADMIN_ROLES, ...roles]}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <LazyMotion features={domAnimation} strict>
        {/* Global reduced-motion honoring — `reducedMotion="user"` makes
            EVERY framer `m.*` animation in the app respect the OS
            prefers-reduced-motion setting (transform/position animations are
            auto-reduced; opacity is kept), instead of relying on each
            component to call useReducedMotion. WCAG 2.3.3. Only affects users
            who opted into reduced motion; everyone else is unchanged. */}
        <MotionConfig reducedMotion="user">
        <BrowserRouter>
          <ErrorBoundary>
            {/* Skip-to-content link — WCAG 2.1 Level A */}
            <a
              href="#main-content"
              className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:rounded-lg focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              Skip to main content
            </a>
            <AuthInitializer />
            <ScopeChangeModal />
            <CreditBlockModal />
            <EditConflictModal />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                {/* Public: where an outward question's answer link leads. The
                    recipient has no account, so this sits OUTSIDE ProtectedPage. */}
                <Route path="/answer/:token" element={<AnswerQuestion />} />
                {/* Public: an NDA signing link (row d4ca72a2). The signer has no
                    account; the token is the boundary. OUTSIDE ProtectedPage. */}
                <Route path="/nda/sign/:token" element={<NDASign />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/status" element={<StatusPage />} />

                {/* Legal documents — public, no auth, source of truth in docs/legal */}
                <Route path="/legal/:slug" element={<Legal />} />
                <Route path="/legal" element={<Navigate to="/legal/terms" replace />} />

                {/* Support contact form — public */}
                <Route path="/support" element={<Support />} />
                {/* Known limits — public, bundled from docs/KNOWN_LIMITS.md (row 16439c4c) */}
                <Route path="/known-limits" element={<KnownLimits />} />

                {/* FTL Living App public directory + per-app Felix changelog
                    (V3 Phase 11 / P4) — public, no auth */}
                <Route path="/living-apps" element={<LivingAppsDirectory />} />
                <Route
                  path="/living-apps/:appSlug"
                  element={<LivingAppsDirectory />}
                />

                {/* Meeting routes — guest join is public, room/lobby allow guest JWTs */}
                <Route path="/join/:meetingId" element={<MeetingJoinGuest />} />
                <Route path="/meeting/:meetingId/end" element={<GuestMeetingEnd />} />
                <Route path="/meeting/:meetingId" element={<MeetingPage shell={false}><MeetingRoom /></MeetingPage>} />
                <Route path="/meeting/:meetingId/lobby" element={<MeetingPage><MeetingLobby /></MeetingPage>} />

                {/* Home: Landing for visitors, Dashboard for authenticated */}
                <Route path="/" element={<HomePage />} />
                <Route path="/portfolio" element={<ProtectedPage><PortfolioHub /></ProtectedPage>} />
                <Route path="/get-started" element={<ProtectedPage><ActivationWizard /></ProtectedPage>} />
                <Route path="/projects" element={<ProtectedPage><Projects /></ProtectedPage>} />

                {/* V3 Phase 12 — template marketplace / gallery (org-scoped;
                    public templates cross-org readable via RLS) */}
                <Route path="/marketplace" element={<ProtectedPage><MarketplaceTemplates /></ProtectedPage>} />

                {/* Phase 5 (UX v2): Living Briefings — personal pages */}
                <Route path="/my-day" element={<ProtectedPage><MyDayPage /></ProtectedPage>} />
                <Route path="/my-week" element={<ProtectedPage><MyWeekPage /></ProtectedPage>} />
                {/* Org-scoped briefing rolls-ups (Phase 5) */}
                <Route path="/risks-and-blockers" element={<ProtectedPage><RisksAndBlockersPage /></ProtectedPage>} />
                <Route path="/decisions-needed" element={<ProtectedPage><DecisionsNeededPage /></ProtectedPage>} />
                <Route path="/change-summary" element={<ProtectedPage><ChangeSummaryPage /></ProtectedPage>} />

                {/* Phase 4 (UX v2): Portfolio → Program → Project hierarchy.
                    Pages render a flag-off fallback when ftl.ux.v2.portfolios /
                    .programs are disabled — no need for route-level gating.
                    /programs/:programId is the per-program detail surface. */}
                <Route path="/portfolios" element={<ProtectedPage><Portfolios /></ProtectedPage>} />
                <Route path="/portfolios/:portfolioId" element={<ProtectedPage><PortfolioDetail /></ProtectedPage>} />
                <Route path="/portfolios/:portfolioId/exceptions" element={<ProtectedPage><PortfolioExceptionsPage /></ProtectedPage>} />
                <Route path="/programs" element={<ProtectedPage><Programs /></ProtectedPage>} />
                <Route path="/programs/:programId" element={<ProtectedPage><ProgramDetail /></ProtectedPage>} />
                {/* ProgramHealth.tsx documents this exact path in its own header
                    and reads `programId` from useParams — but App.tsx had NO
                    reference to the component at all, so the page could not be
                    reached by any URL. The backend half has been live since
                    2026-05-27: _generate_program_health is a real SQL rollup and
                    GET /assistant/briefings/program_health is mounted with a 1h
                    TTL. Only the route was missing. */}
                <Route path="/programs/:programId/health" element={<ProtectedPage><ProgramHealthPage /></ProtectedPage>} />

                {/* Project-scoped routes — nested under /projects/:projectId */}
                <Route
                  path="/projects/:projectId"
                  element={<ProtectedPage><ProjectLayout /></ProtectedPage>}
                >
                  <Route index element={<ProjectLanding />} />
                  {/* Phase 5 (UX v2): Living Briefings — project-scoped pages */}
                  <Route path="health" element={<ProjectHealthPage />} />
                  <Route path="sponsor-update" element={<SponsorUpdatePage />} />
                  <Route path="risks-and-blockers" element={<RisksAndBlockersPage />} />
                  <Route path="decisions-needed" element={<DecisionsNeededPage />} />
                  <Route path="change-summary" element={<ChangeSummaryPage />} />
                  <Route path="delivery-team-update" element={<DeliveryTeamUpdatePage />} />
                  <Route path="plan" element={<ProjectPlan />} />
                  <Route path="objectives" element={<Objectives />} />
                  <Route path="modules" element={<Modules />} />
                  <Route path="components" element={<Components />} />
                  <Route path="specifications" element={<ComponentSpecifications />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="tests" element={<Tests />} />
                  <Route path="test-cases" element={<Tests />} />
                  <Route path="defects" element={<Defects />} />
                  <Route path="documents" element={<Documents />} />
                  <Route path="demo-check" element={<DemoCheck />} />
                  <Route path="traceability" element={<Traceability />} />
                  <Route path="baselines" element={<Baselines />} />
                  <Route path="change-control" element={<ChangeControl />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="meetings" element={<Meetings />} />
                  <Route path="meeting-history" element={<MeetingHistory />} />
                  <Route path="meetings/:meetingId/review" element={<MeetingReview />} />
                  <Route path="conflicts" element={<Conflicts />} />
                  <Route path="sprints" element={<Sprints />} />
                  <Route path="secrets" element={<SecretsVaultPage />} />
                  <Route path="import" element={<RequirementsImport />} />
                  <Route path="document-plan" element={<DocumentPlan />} />
                  <Route path="milestones" element={<Milestones />} />
                  <Route path="user-stories" element={<UserStories />} />
                  <Route path="personas" element={<UserPersonas />} />
                  <Route path="qa-sessions" element={<QASessions />} />
                  <Route path="action-items" element={<QAActionItems />} />
                  <Route path="knowledge-base" element={<KnowledgeBase />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="qa-agent" element={<ProjectSurfaceGate><QAAgent /></ProjectSurfaceGate>} />
                  <Route path="felix-runs" element={<FelixRuns />} />
                  <Route path="felix-runs/:runId" element={<FelixRunDetail />} />
                  <Route path="git-config" element={<ProjectSurfaceGate><GitConfiguration /></ProjectSurfaceGate>} />
                </Route>

                {/* Legacy flat routes — redirect to project-scoped if a project is stored */}
                <Route path="/objectives" element={<ProtectedPage><Objectives /></ProtectedPage>} />
                <Route path="/tasks" element={<ProtectedPage><Tasks /></ProtectedPage>} />
                <Route path="/tests" element={<ProtectedPage><Tests /></ProtectedPage>} />
                <Route path="/test-cases" element={<ProtectedPage><Tests /></ProtectedPage>} />
                <Route path="/defects" element={<ProtectedPage><Defects /></ProtectedPage>} />
                <Route path="/documents" element={<ProtectedPage><Documents /></ProtectedPage>} />
                <Route path="/traceability" element={<ProtectedPage><Traceability /></ProtectedPage>} />
                <Route path="/baselines" element={<ProtectedPage><Baselines /></ProtectedPage>} />
                <Route path="/change-control" element={<ProtectedPage><ChangeControl /></ProtectedPage>} />
                {/* Project-less meeting review — a completed ad-hoc meeting has no
                    project namespace; MeetingReview reads only :meetingId, so this
                    renders identically to the project-scoped child route. */}
                <Route path="/meetings/:meetingId/review" element={<ProtectedPage><MeetingReview /></ProtectedPage>} />
                {/* User profile, team & billing */}
                <Route path="/profile" element={<ProtectedPage><UserProfile /></ProtectedPage>} />
                <Route path="/profile/inventor-record" element={<ProtectedPage><InventorPriorExperience /></ProtectedPage>} />
                <Route path="/team" element={<ProtectedPage><Team /></ProtectedPage>} />
                <Route path="/billing" element={<ProtectedPage><Billing /></ProtectedPage>} />
                <Route path="/billing/success" element={<ProtectedPage><BillingSuccess /></ProtectedPage>} />
                {/* Stripe returns here after a DOMAIN purchase. Both routes were
                    missing, so a customer who had just paid fell through to the
                    catch-all NotFound below. See domains.py:_DOMAIN_CHECKOUT_*_URL. */}
                <Route path="/domains/checkout/success" element={<ProtectedPage><DomainCheckoutSuccess /></ProtectedPage>} />
                <Route path="/domains/checkout/cancel" element={<ProtectedPage><DomainCheckoutCancel /></ProtectedPage>} />
                <Route path="/billing/cancel" element={<ProtectedPage><BillingCancel /></ProtectedPage>} />
                <Route path="/release-notes" element={<ProtectedPage><ReleaseNotes /></ProtectedPage>} />
                <Route path="/reports" element={<ProtectedPage><OrgReports /></ProtectedPage>} />
                <Route path="/staffing-simulation" element={<ProtectedPage><StaffingSimulationPage /></ProtectedPage>} />
                <Route path="/what-gives" element={<ProtectedPage><WhatGivesPage /></ProtectedPage>} />
                <Route path="/what-gives/:id" element={<ProtectedPage><WhatGivesPage /></ProtectedPage>} />
                <Route path="/labs/founder-review" element={<ProtectedPage><FounderReviewPage /></ProtectedPage>} />
                <Route path="/skills" element={<ProtectedPage><SkillsPage /></ProtectedPage>} />
                <Route path="/design-system" element={<ProtectedPage><DesignSystem /></ProtectedPage>} />
                <Route path="/action-items" element={<ProtectedPage><QAActionItems /></ProtectedPage>} />
                <Route path="/conversations" element={<ProtectedPage><ConversationHistory /></ProtectedPage>} />
                {/* Google Workspace */}
                <Route path="/workspace" element={<ProtectedPage><WorkspaceHub /></ProtectedPage>} />
                <Route path="/gmail" element={<ProtectedPage><GmailPage /></ProtectedPage>} />
                <Route path="/calendar" element={<ProtectedPage><CalendarPage /></ProtectedPage>} />
                <Route path="/contacts" element={<ProtectedPage><ContactsPage /></ProtectedPage>} />
                <Route path="/drive" element={<ProtectedPage><DrivePage /></ProtectedPage>} />

                {/* Unified Organization Settings (replaces scattered settings pages) */}
                <Route path="/organization/settings" element={<AdminPage><OrganizationSettings /></AdminPage>} />
                <Route path="/organization/felix-controls" element={<AdminPage><OrgFelixSteering /></AdminPage>} />
                {/* V2 Redesign internal showcase — DP-1 gate prep, admin-only */}
                {/* Legacy routes — redirect to unified page with domain context */}
                <Route path="/admin/settings" element={<Navigate to="/organization/settings?domain=ai" replace />} />
                <Route path="/settings" element={<Navigate to="/organization/settings" replace />} />
                <Route path="/admin/branding" element={<Navigate to="/organization/settings?domain=appearance" replace />} />
                <Route path="/branding" element={<Navigate to="/organization/settings?domain=appearance" replace />} />
                <Route path="/status-registry" element={<Navigate to="/organization/settings?domain=workflow" replace />} />
                <Route path="/success-policies" element={<Navigate to="/organization/settings?domain=quality" replace />} />
                <Route path="/roles" element={<AdminPage><Roles /></AdminPage>} />
                {/* PROJECT_OWNER, because the API says so. Every mutating
                    route in webhook_endpoints.py is
                    `Depends(require_role("PROJECT_OWNER"))` (:97, :153, :204),
                    and the SSRF guard at :73 was written FOR that audience —
                    "so a PROJECT_OWNER cannot point the org's webhooks at
                    internal services or the cloud-metadata endpoint". The UI
                    was stricter than the API it fronts, so the 3 PROJECT_OWNERs
                    the backend authorises were redirected to '/' before they
                    could reach the page. Scoped to this route rather than
                    widened into ADMIN_ROLES, which eleven other routes share. */}
                <Route path="/admin/webhooks" element={<AdminPageFor roles={["project_owner"]}><WebhookEndpointsPage /></AdminPageFor>} />
                <Route path="/admin/domain-pricing" element={<AdminPage><DomainPricingPage /></AdminPage>} />
                <Route path="/admin/pricing" element={<AdminPage><PricingConfigPage /></AdminPage>} />
                <Route path="/admin/feature-flags" element={<AdminPage><FeatureFlagsAdminPage /></AdminPage>} />
                {/* /v2/__demo__/home preview route removed (T3 one-home
                    collapse): v2 Home is now only the flag-off fallback
                    inside HomeOrV3 → HomeOrDashboard. */}
                <Route path="/audit-log" element={<AdminPage><AuditLog /></AdminPage>} />
                <Route path="/compliance" element={<ProtectedPage><Compliance /></ProtectedPage>} />
                <Route path="/admin/users" element={<AdminPage><UserManagement /></AdminPage>} />
                <Route path="/admin/access" element={<AdminPage><AccessControl /></AdminPage>} />
                <Route path="/admin/feedback" element={<AdminPage><FeedbackReport /></AdminPage>} />
                <Route path="/feedback" element={<ProtectedPage><FeedbackReport /></ProtectedPage>} />
                {/* Deep link from a notification. The action_url shape is
                    /feedback/{id} and 973 of the ~1074 notifications carrying one
                    use it — without this route every one of them 404s, because the
                    exact "/feedback" path above will not match a trailing id. */}
                <Route path="/feedback/:feedbackId" element={<ProtectedPage><FeedbackReport /></ProtectedPage>} />
                {/* Row 79d0e9e0: a retention-disposal notice links here, so its owners
                    can see what will be deleted and when and download a copy first. */}
                <Route path="/retention-notices/:batchId" element={<ProtectedPage><RetentionNoticePage /></ProtectedPage>} />
                <Route path="/user-guides" element={<ProtectedPage><UserGuides /></ProtectedPage>} />

                {/* Platform Admin routes (global admin only) */}
                <Route path="/platform" element={<ProtectedPage><PlatformDashboard /></ProtectedPage>} />
                <Route path="/platform/patent-docket" element={<ProtectedPage><PlatformPatentDocket /></ProtectedPage>} />
                <Route path="/platform/organizations" element={<ProtectedPage><PlatformOrganizations /></ProtectedPage>} />
                <Route path="/platform/users" element={<ProtectedPage><PlatformUsers /></ProtectedPage>} />
                <Route path="/platform/access-requests" element={<ProtectedPage><PlatformAccessRequests /></ProtectedPage>} />
                <Route path="/platform/api-keys" element={<ProtectedPage><PlatformApiKeys /></ProtectedPage>} />
                <Route path="/platform/support" element={<ProtectedPage><PlatformSupportInbox /></ProtectedPage>} />
                <Route path="/platform/trial-codes" element={<ProtectedPage><PlatformTrialCodes /></ProtectedPage>} />
                {/* StatusRegistry was lazy-imported at :146 and bound to NO
                    <Route> — a complete page, built into every bundle, that
                    nobody could open. Its endpoints have been mounted the whole
                    time (GET/POST /admin/status-config/, GET /registry,
                    GET /transitions, PUT /{id}) and the write half is
                    require_platform_admin, so it belongs here rather than under
                    the org settings redirect at :534: /status-registry points at
                    WorkflowDomain, which calls only statusConfigApi.list() and
                    renders a read-only card. Nothing anywhere called
                    statusConfigApi.create or .update except this page.

                    WHY IT MATTERS RIGHT NOW: ARCHIVED is reachable only from
                    SKETCH for user_story, objective and component_specification,
                    and SKETCH holds ZERO rows in production — so the archive
                    control cannot render for 2,888 approved stories, 3,250
                    approved tasks, 1,777 approved test cases or 477 approved
                    specs. Which statuses may retire a record is a product rule,
                    and this is the console that edits it. */}
                <Route path="/platform/status-registry" element={<ProtectedPage><StatusRegistry /></ProtectedPage>} />
                <Route path="/platform/financials" element={<Navigate to="/platform?tab=financials" replace />} />
                <Route path="/platform/cloud-spend" element={<Navigate to="/platform?tab=cloud-spend" replace />} />
                <Route path="/platform/configuration" element={<ProtectedPage><PlatformConfiguration /></ProtectedPage>} />
                <Route path="/platform/feature-flags" element={<ProtectedPage><PlatformFeatureFlags /></ProtectedPage>} />
                <Route path="/platform/felix-ops" element={<ProtectedPage><PlatformFelixOps /></ProtectedPage>} />
                <Route path="/platform/pricing" element={<ProtectedPage><PlatformPricing /></ProtectedPage>} />
                <Route path="/platform/supervisor" element={<ProtectedPage><PlatformSupervisor /></ProtectedPage>} />
                <Route path="/platform/feedback" element={<ProtectedPage><PlatformFeedback /></ProtectedPage>} />
                <Route path="/platform/nda" element={<ProtectedPage><PlatformNDA /></ProtectedPage>} />
                <Route path="/platform/processes" element={<Navigate to="/platform?tab=processes" replace />} />
                <Route path="/platform/release-notes" element={<Navigate to="/platform?tab=release-notes" replace />} />
                <Route path="/platform/reports" element={<Navigate to="/platform?tab=reports" replace />} />
                <Route path="/platform/workspaces" element={<Navigate to="/platform?tab=workspaces" replace />} />
                <Route path="/platform/conversation-insights" element={<Navigate to="/platform?tab=insights" replace />} />
                <Route path="/platform/felix-benchmarks" element={<Navigate to="/platform?tab=ai-quality" replace />} />
                <Route path="/platform/felix-config" element={<ProtectedPage><PlatformFelixConfig /></ProtectedPage>} />
                <Route path="/platform/felix-quality" element={<ProtectedPage><PlatformFelixQuality /></ProtectedPage>} />
                <Route path="/platform/felix-steering" element={<ProtectedPage><PlatformFelixSteering /></ProtectedPage>} />
                <Route path="/platform/friction-findings" element={<ProtectedPage><PlatformFrictionFindings /></ProtectedPage>} />
                <Route path="/platform/judge-calibration" element={<ProtectedPage><PlatformJudgeCalibration /></ProtectedPage>} />
                <Route path="/platform/uat-pack" element={<ProtectedPage><PlatformUatPack /></ProtectedPage>} />
                {/* QA Agent + QA Diagnostics absorbed into the Test Harness
                    hub as tabs (2026-05-01). Old paths redirect so deep
                    links from past Issues / Slack messages still work. */}
                <Route path="/platform/qa-agent" element={<Navigate to="/platform/felix-quality?tab=qa-agent" replace />} />
                <Route path="/platform/qa-diagnostics" element={<Navigate to="/platform/felix-quality?tab=diagnostics" replace />} />
                {/* Phase 0 (v3) — inventor-only patent portfolio. Deep-link-only. */}
                <Route path="/patents/portfolio" element={<ProtectedPage><PatentsPortfolio /></ProtectedPage>} />

                {/* Catch-all */}
                {/* V3 Phase 1 — designer-tunable motion vocabulary surface */}
                <Route path="/motion/phase1-showcase" element={<ProtectedPage><MotionShowcase /></ProtectedPage>} />
                {/* V3 Phase 4 — CrystallizationKernel WOW visualization showcase (Patent J) */}
                <Route path="/motion/phase4-crystallization-showcase" element={<ProtectedPage><Phase4Showcase /></ProtectedPage>} />
                {/* V3 Phase 2 Iterator backend test surface is deliberately NOT mounted.
                    It is a developer harness: it can open a session, append transcripts
                    and commit iterations against a real project from a raw panel,
                    bypassing the product's own flow. It was gated on
                    ftl.ux.v3.iterator_backend; that flag was retired (migration 480,
                    0 rows) and nothing replaced the gate, so it shipped in every
                    customer bundle. Measured before removing: 0 telemetry events of
                    9,180 total, and 0 iterator sessions from it. The page and its tests
                    remain in the tree — restoring it is one import and one Route, and
                    the guard test asserts it stays unmounted so it cannot return
                    silently. */}
                {/* Living Interface design-language showcase (review surface) */}
                <Route path="/design-language" element={<ProtectedPage><DesignLanguage /></ProtectedPage>} />
                {/* V3 Phase 3 — Iterator MVP fullscreen route (gated by ftl.ux.v3.iterator_mvp) */}
                <Route path="/projects/:projectId/develop" element={<ProtectedPage><ProjectSurfaceGate><DevelopIterator /></ProjectSurfaceGate></ProtectedPage>} />
                <Route path="/projects/:projectId/iterator" element={<ProtectedPage><ProjectSurfaceGate><Iterator /></ProjectSurfaceGate></ProtectedPage>} />
                {/* V3 Phase 9 — DomainSelector naming + checkout (gated by ftl.ux.v3.domain_selector) */}
                <Route path="/projects/:projectId/domain" element={<ProtectedPage><ProjectSurfaceGate><DomainSelectorRoute /></ProjectSurfaceGate></ProtectedPage>} />
                {/* V3 Phase 10 — ProvisioningCanvas (gated by ftl.ux.v3.tenant_hosting) */}
                <Route path="/projects/:projectId/hosting" element={<ProtectedPage><ProjectSurfaceGate><HostingRoute /></ProjectSurfaceGate></ProtectedPage>} />
                <Route path="/admin/github-app" element={<AdminPage><GitHubAppSetup /></AdminPage>} />
                {/* GitHub App manifest setup_url — where a customer lands after installing
                    the FTL App. Reads installation_id and records the connection. NOTE:
                    /settings (line above) is an EXACT-match redirect, so it does not cover
                    this deeper path; without its own route the catch-all would 404 it. */}
                <Route path="/settings/integrations" element={<ProtectedPage><SettingsIntegrations /></ProtectedPage>} />
                {/* V3 Phase 10 P2 — Embedded Felix tenant-config (gated by ftl.ux.v3.embedded_felix + a deployment) */}
                <Route path="/projects/:projectId/embedded-felix" element={<ProtectedPage><ProjectSurfaceGate><EmbeddedFelixRoute /></ProjectSurfaceGate></ProtectedPage>} />
                {/* V3 Phase 11 — Operate surface (gated by ftl.ux.v3.operate_mode + an active deployment) */}
                <Route path="/projects/:projectId/gates" element={<ProtectedPage><ProgramGates /></ProtectedPage>} />
                <Route path="/projects/:projectId/budget" element={<ProtectedPage><ProjectBudget /></ProtectedPage>} />
                <Route path="/projects/:projectId/operate" element={<ProtectedPage><ProjectSurfaceGate><OperateRoute /></ProjectSurfaceGate></ProtectedPage>} />
                {/* V3 Phase 15 — Broadcast surface (gated by ftl.ux.v3.broadcast_mode) */}
                <Route path="/projects/:projectId/broadcast" element={<ProtectedPage><ProjectSurfaceGate><BroadcastRoute /></ProjectSurfaceGate></ProtectedPage>} />
                <Route path="/marketing/leads" element={<ProtectedPage><LeadsSurface /></ProtectedPage>} />
                <Route path="/marketing/segments" element={<ProtectedPage><SegmentBuilder /></ProtectedPage>} />
                <Route path="/marketing/templates" element={<ProtectedPage><CampaignTemplates /></ProtectedPage>} />
                <Route path="/marketing/analytics" element={<ProtectedPage><MarketingAnalytics /></ProtectedPage>} />
                <Route path="/marketing/content" element={<ProtectedPage><ContentStudio /></ProtectedPage>} />
                <Route path="/marketing/approvals" element={<ProtectedPage><ApprovalsQueue /></ProtectedPage>} />
                <Route path="/marketing/social" element={<ProtectedPage><SocialComposer /></ProtectedPage>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            {/* System-wide maintenance broadcast — fixed top, polled
                every 15s, renders only when backend reports active.
                Mounted before CookieConsentBanner so it sits above
                everything including the cookie banner during a deploy. */}
            <MaintenanceBanner />
            {/* A tab running the bundle we replaced. Mounted beside the
                maintenance banner because they share a corner and must not
                both appear: the deploy strip speaks during a deploy, this one
                after it. */}
            <NewVersionAvailable deploying={isDeploying()} />
            {/* GDPR/CCPA cookie consent — renders globally on first visit */}
            <CookieConsentBanner />
            {/* Re-consent prompt when the terms version in force has moved past
                what a signed-in user accepted. Renders nothing for anonymous
                visitors, nothing when up to date, and nothing on an API error —
                a legal gate that fails closed would lock users out of the
                product during an unrelated outage. */}
            <TermsReacceptGate />
          </ErrorBoundary>
        </BrowserRouter>
        </MotionConfig>
        </LazyMotion>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App;
